export type ScaleValue = 0.25 | 0.5 | 1 | 2 | 3 | 4;

export type ScaleGroup = "original" | "retina" | "smaller";

export interface ScaleOption {
  value: ScaleValue;
  title: string;
  group: ScaleGroup;
}

export const SCALE_OPTIONS: ReadonlyArray<ScaleOption> = [
  { value: 1, title: "1×", group: "original" },
  { value: 2, title: "2×", group: "retina" },
  { value: 3, title: "3×", group: "retina" },
  { value: 4, title: "4×", group: "retina" },
  { value: 0.5, title: "0.5×", group: "smaller" },
  { value: 0.25, title: "0.25×", group: "smaller" }
];

export const SCALE_GROUP_LABELS: Record<ScaleGroup, string> = {
  original: "Original Size",
  retina: "Retina Sizes",
  smaller: "Smaller Size"
};

export const DEFAULT_SCALE: ScaleValue = 1;

// Renderer rejects 0.25/0.5 for designs below ~1m² @ 96 DPI.
// Mirrors src/constants/Scale.js in the renderer.
export const MIN_DOWNSCALE_AREA_PX = 16_000_000;

// `width`/`height` here must be **pixels at 96 DPI** — same units the renderer's
// Scale.js gate uses. Both `designData` (full design) and `design` (list/card
// entity) expose pixel dimensions in this codebase, so either shape is accepted.
// `bleed` is the per-side trim in pixels — to get the effective raster size,
// subtract `2 * bleed` from each axis (bleed is trimmed off both edges).
interface DesignDimensions {
  width?: number;
  height?: number;
  bleed?: number;
  measurementUnits?: string;
}

// Returns true when the design's base pixel area meets the renderer's 1m²-ish
// floor for 0.25×/0.5× downscale. Returns false for missing or zero dimensions,
// so partially-hydrated card popouts safely hide the downscale options.
export const isEligibleForDownscale = (
  designData: DesignDimensions | null | undefined
): boolean => {
  if (!designData) return false;
  const { width, height } = designData;
  if (!width || !height) return false;
  return width * height >= MIN_DOWNSCALE_AREA_PX;
};

type DownloadFormat = "pdf" | "jpg" | "png" | string;

// Filters SCALE_OPTIONS down to what the renderer will accept for the given
// format and design. Eligible designs (≥1m²) swap retina (2×/3×/4×) for
// downscales (0.5×/0.25×) — at that base size, 4× would exceed 15k px on a
// side, which is rarely a useful output. PDF never gets retina at all.
export const getAvailableScaleOptions = (
  designData: DesignDimensions | null | undefined,
  format: DownloadFormat
): ReadonlyArray<ScaleOption> => {
  const eligibleForDownscale = isEligibleForDownscale(designData);
  return SCALE_OPTIONS.filter(option => {
    if (option.value < 1) return eligibleForDownscale;
    if (option.value > 1) return format !== "pdf" && !eligibleForDownscale;
    return true;
  });
};

// Returns "W × Hpx" sized at the given scale, for the projected-dimensions hint
// next to each option. Output is always pixels — raster output is pixel-based
// regardless of source `measurementUnits`. Subtracts bleed from both edges of
// each axis (`2 * bleed`) since the renderer trims it off all four sides —
// e.g. a 1000×1000mm design at 1× reports 3803px raw but downloads at 3779px
// after a 12px per-side bleed trim (3803 − 2×12 = 3779).
export const formatProjectedDimensions = (
  designData: DesignDimensions | null | undefined,
  scale: ScaleValue
): string | null => {
  if (!designData) return null;
  const { width, height, bleed = 0 } = designData;
  if (!width || !height || width <= 0 || height <= 0) return null;
  const totalBleed = 2 * bleed;
  const effectiveWidth = Math.max(0, width - totalBleed);
  const effectiveHeight = Math.max(0, height - totalBleed);
  // Renderer uses floor() for downscale (matches src/services/imageService.js);
  // round() for upscale keeps display sensible for non-integer source dimensions.
  const reduce = scale < 1 ? Math.floor : Math.round;
  return `${reduce(effectiveWidth * scale)} × ${reduce(effectiveHeight * scale)}px`;
};
